/**Import react section */
import React, { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import propTypes from 'prop-types'

/**Import material ui section */
import { Select, MenuItem, Typography } from '@material-ui/core'

/**Import components/helpers section */
import { hasAuthorization } from '../../../store/session/sessionActions'
import { getLoggedUserProfileSelector } from '../../../store/session/sessionSelectors'
import { PORTAL_ADMINISTRATOR } from '../../../resources/RolesEnum'
import { GetPriorityOptions } from '../../../store/helpers/FilterHelper'
import { useListingIsReadOnly } from '../resources/ListingEditabilityContext'
import { updatePriority } from '../../../store/services/serviceActions'
import ConfirmFieldUpdateDialog from './ConfirmFieldUpdateDialog'
import Toaster from '../../common/Toaster'

/**
 * Inline editable cell for the Priority column (services listing).
 *
 * Mirrors the Work Process Status cell: a select that holds the chosen value aside until the user
 * confirms it, so a mis-click does not write to the server.
 */
const PriorityCell = (props) => {
	const { item } = props
	const dispatch = useDispatch()
	const userProfile = useSelector(getLoggedUserProfileSelector)
	const isSavingService = useSelector((state) => state.services.get('isSavingService'))
	const options = GetPriorityOptions()
	const isAuthorizedToEdit = hasAuthorization(userProfile, [PORTAL_ADMINISTRATOR])
	const isReadOnlyListing = useListingIsReadOnly()
	const currentOption = options.find((option) => option.key === Number(item.Priority))

	/**Value selected but not yet confirmed; null means no pending confirmation (Select falls back to item.Priority) */
	const [pendingValue, setPendingValue] = useState(null)
	const pendingOption = options.find((option) => option.key === pendingValue)
	const [toasterOptions, setToasterOptions] = useState({ open: false, variant: null, message: null })

	const onChangePriority = (event) => {
		if (event.target.value === '') return
		setPendingValue(Number(event.target.value))
	}

	const onCancelConfirmation = () => {
		setPendingValue(null)
	}

	const onConfirmPriority = () => {
		dispatch(updatePriority(item, pendingValue))
			.then(() => setPendingValue(null))
			.catch((errorMessage) => {
				setPendingValue(null)
				setToasterOptions({ open: true, variant: 'error', message: errorMessage || 'Error al actualizar Priority' })
			})
	}

	// The closed-services listing assigns no priority: the work is over, so urgency has nothing left
	// to order. The condition comes from the listing and not from the row, which is what keeps the
	// active listing editable whatever statuses its rows happen to carry. The same branch covers the
	// roles that may not edit at all.
	if (!isAuthorizedToEdit || isReadOnlyListing) {
		return <Typography variant='subtitle2'>{currentOption ? currentOption.label : ''}</Typography>
	}

	return (
		<>
			<Select
				value={pendingValue !== null ? pendingValue : item.Priority || ''}
				onChange={onChangePriority}
				onClick={(event) => event.stopPropagation()}
				displayEmpty
				margin='dense'
			>
				<MenuItem value=''>
					<em>—</em>
				</MenuItem>
				{options.map((option) => (
					<MenuItem key={option.key} value={option.key}>
						{option.label}
					</MenuItem>
				))}
			</Select>
			{pendingValue !== null && (
				<ConfirmFieldUpdateDialog
					open={pendingValue !== null}
					onClose={onCancelConfirmation}
					onConfirm={onConfirmPriority}
					isSaving={isSavingService}
					ticket={item.Ticket}
					newValueLabel={pendingOption ? pendingOption.label : ''}
					fieldName='Priority'
				/>
			)}
			{toasterOptions.open && <Toaster toasterOptions={toasterOptions} onClose={() => setToasterOptions({ open: false })} />}
		</>
	)
}

PriorityCell.propTypes = {
	item: propTypes.shape({
		Token: propTypes.string,
		Ticket: propTypes.any,
		Status: propTypes.oneOfType([propTypes.string, propTypes.number]),
		Priority: propTypes.oneOfType([propTypes.string, propTypes.number])
	}).isRequired
}

export default PriorityCell
